import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Image as ImageIcon, Upload, Trash2, Copy, Check, Loader2, Film } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_IMAGE = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
const ALLOWED_VIDEO = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska', 'video/ogg'];
const ALLOWED = [...ALLOWED_IMAGE, ...ALLOWED_VIDEO];
const PER_FILE_MAX = 100 * 1024 * 1024; // 100 MB per file

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const AdminGallery = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/gallery`, { withCredentials: true });
      setItems(data.items || []);
      setQuota(data.quota || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const usedPct = useMemo(() => {
    if (!quota || !quota.total_bytes) return 0;
    return Math.min(100, Math.round((quota.used_bytes / quota.total_bytes) * 100));
  }, [quota]);

  const handleSelectFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadAll(files);
    e.target.value = '';
  };

  const uploadOne = async (file) => {
    if (!ALLOWED.includes(file.type)) {
      toast.error(`${file.name}: only images (PNG, JPG, WEBP, GIF) and videos (MP4, WEBM, MOV) are allowed.`);
      return false;
    }
    if (file.size > PER_FILE_MAX) {
      toast.error(`${file.name}: too large (max 100 MB per file).`);
      return false;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/admin/gallery`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setItems((prev) => [data.item, ...prev]);
      setQuota(data.quota);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Upload failed: ${file.name}`);
      return false;
    }
  };

  const uploadAll = async (files) => {
    setUploading(true);
    let success = 0;
    for (const f of files) {
      // Re-check quota after each upload — if backend rejects, loop stops on toast
      // eslint-disable-next-line no-await-in-loop
      const ok = await uploadOne(f);
      if (ok) success += 1;
    }
    setUploading(false);
    if (success) toast.success(`${success} file${success === 1 ? '' : 's'} uploaded.`);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this ${item.kind === 'video' ? 'video' : 'image'}?\n\n${item.original_filename || item.filename}`)) return;
    try {
      const { data } = await axios.delete(`${API}/api/admin/gallery/${item.file_id}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.file_id !== item.file_id));
      setQuota(data.quota);
      toast.success('File deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const copyUrl = async (item) => {
    const absolute = `${API}${item.url}`;
    try {
      await navigator.clipboard.writeText(absolute);
      setCopiedId(item.file_id);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedId((id) => (id === item.file_id ? null : id)), 1500);
    } catch {
      toast.error('Could not copy. Long-press the image and copy URL manually.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isFull = quota && quota.remaining_bytes <= 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => navigate('/admin')} data-testid="gallery-back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <h1 className="text-3xl font-black font-heading flex items-center gap-2" data-testid="admin-gallery-heading">
              <ImageIcon className="h-7 w-7 text-primary" /> Media Gallery
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime,video/x-matroska,video/ogg"
              multiple
              hidden
              onChange={handleSelectFiles}
              data-testid="gallery-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || isFull}
              data-testid="gallery-upload-button"
            >
              {uploading ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</>) : (<><Upload className="h-4 w-4 mr-1" /> Upload Media</>)}
            </Button>
          </div>
        </div>

        {/* Quota bar */}
        <section className="bg-white rounded-xl border border-[#E0E0E0] p-5 mb-8" data-testid="gallery-quota-card">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className="text-sm font-bold text-foreground">Storage</div>
              <div className="text-xs text-muted-foreground">Images (PNG, JPG, WEBP, GIF) &amp; videos (MP4, WEBM, MOV) · max 100 MB per file · 500 MB total</div>
            </div>
            <div className="text-sm tabular-nums" data-testid="gallery-quota-text">
              <span className={`font-bold ${isFull ? 'text-red-700' : 'text-foreground'}`}>{fmtBytes(quota?.used_bytes || 0)}</span>
              <span className="text-muted-foreground"> / {fmtBytes(quota?.total_bytes || 0)} used</span>
              {quota && (
                <span className="text-muted-foreground"> · {fmtBytes(quota.remaining_bytes)} free</span>
              )}
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-[#F0F0F0] overflow-hidden">
            <div
              className={`h-full transition-all ${usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-amber-500' : 'bg-primary'}`}
              style={{ width: `${usedPct}%` }}
              data-testid="gallery-quota-bar"
            />
          </div>
          {isFull && (
            <div className="mt-3 text-sm text-red-700 font-medium" data-testid="gallery-full-warning">
              Gallery is full. Delete some files before uploading new ones.
            </div>
          )}
        </section>

        {/* Grid */}
        <section className="bg-white rounded-xl border border-[#E0E0E0] p-5" data-testid="gallery-grid-card">
          {items.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="font-medium text-foreground">No media yet</div>
              <div className="text-sm">Click &quot;Upload Media&quot; to add your first image or video.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="gallery-grid">
              {items.map((item) => {
                const isVideo = item.kind === 'video' || (item.content_type || '').startsWith('video/');
                return (
                  <div
                    key={item.file_id}
                    className="group relative aspect-square rounded-lg overflow-hidden border border-[#E5E5E5] bg-[#0a0a0a]"
                    data-testid={`gallery-item-${item.file_id}`}
                  >
                    {isVideo ? (
                      <>
                        <video
                          src={`${API}${item.url}`}
                          className="w-full h-full object-cover"
                          controls
                          preload="metadata"
                          data-testid={`gallery-video-${item.file_id}`}
                        />
                        <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-white font-semibold uppercase tracking-wide">
                          <Film className="h-3 w-3" /> Video
                        </div>
                      </>
                    ) : (
                      <img
                        src={`${API}${item.url}`}
                        alt={item.original_filename || item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className={`absolute inset-0 ${isVideo ? 'bg-black/0 group-hover:bg-black/55' : 'bg-black/0 group-hover:bg-black/45'} transition-colors duration-200 flex items-end justify-center gap-2 p-2 opacity-0 group-hover:opacity-100 pointer-events-none`}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => copyUrl(item)}
                        className="bg-white text-foreground hover:bg-white/90 pointer-events-auto"
                        data-testid={`gallery-copy-${item.file_id}`}
                      >
                        {copiedId === item.file_id ? (<><Check className="h-3.5 w-3.5 mr-1 text-green-600" /> Copied</>) : (<><Copy className="h-3.5 w-3.5 mr-1" /> Copy URL</>)}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(item)}
                        className="pointer-events-auto"
                        data-testid={`gallery-delete-${item.file_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminGallery;
